import React, { useState, useEffect } from 'react';
import { X, User, BookOpen, Plus } from 'lucide-react';
import api from '../utils/api';
import FlexibleTimeInput from './FlexibleTimeInput';
import { formatDateTimeForInput, formatDuration } from '../utils/timeFormatting';

interface Student {
  id: string;
  name: string;
  email: string;
  studentProfile?: {
    subjects: Array<{
      id: string;
      name: string;
    }>;
  };
}

interface Subject {
  id: string;
  name: string;
  category: string;
  onlineRate: number;
  inPersonRate: number;
}

interface TutorBookingSuggestionProps {
  isOpen: boolean;
  onClose: () => void;
  onSuggestionCreated: () => void;
  preselectedStudent?: {
    id: string;
    name: string;
    email: string;
    studentProfile?: {
      subjects: Array<{
        id: string;
        name: string;
      }>;
    };
  };
}

const TutorBookingSuggestion: React.FC<TutorBookingSuggestionProps> = ({
  isOpen,
  onClose,
  onSuggestionCreated,
  preselectedStudent
}) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    studentId: '',
    subjectId: '',
    startTime: '',
    endTime: '',
    type: 'LESSON' as 'LESSON' | 'CONSULTATION',
    title: '',
    notes: '',
    isOnline: false
  });

  useEffect(() => {
    if (isOpen) {
      fetchStudents();
      fetchSubjects();

      // Preload student if provided
      if (preselectedStudent) {
        setFormData(prev => ({
          ...prev,
          studentId: preselectedStudent.id
        }));
      }
    }
  }, [isOpen, preselectedStudent]);

  const fetchStudents = async () => {
    try {
      const response = await api.get('/admin/students');
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      setError('Failed to load students');
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get('/subjects');
      setSubjects(response.data);
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      setError('Failed to load subjects');
    }
  };

  const calculatePrice = () => {
    if (!formData.subjectId || !formData.startTime || !formData.endTime) return 0;
    
    const subject = subjects.find(s => s.id === formData.subjectId);
    if (!subject) return 0;
    
    // Calculate duration from start and end times
    const startTime = new Date(formData.startTime);
    const endTime = new Date(formData.endTime);
    const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
    
    if (durationHours <= 0) return 0;
    
    // Consultations use a fixed £5 rate on the backend regardless of duration/subject.
    if (formData.type === 'CONSULTATION') {
      return 5;
    }

    // Regular lessons use subject-specific online/in-person rates.
    const hourlyRate = formData.isOnline ? subject.onlineRate : subject.inPersonRate;
    return hourlyRate * durationHours;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await api.post('/bookings/suggest', formData);
      setSuccess('Booking suggestion sent successfully!');
      onSuggestionCreated();
      
      // Reset form
      setFormData({
        studentId: preselectedStudent?.id || '',
        subjectId: '',
        startTime: '',
        endTime: '',
        type: 'LESSON',
        title: '',
        notes: '',
        isOnline: false
      });
      
      setTimeout(() => {
        onClose();
        setSuccess('');
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to suggest booking');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      studentId: preselectedStudent?.id || '',
      subjectId: '',
      startTime: '',
      endTime: '',
      type: 'LESSON',
      title: '',
      notes: '',
      isOnline: false
    });
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Set default start time to next hour and end time to 1 hour after
  useEffect(() => {
    if (isOpen && !formData.startTime) {
      const now = new Date();
      now.setHours(now.getHours() + 1, 0, 0, 0);
      const endTime = new Date(now);
      endTime.setHours(endTime.getHours() + 1); // Default 1 hour duration
      
      setFormData(prev => ({
        ...prev,
        startTime: formatDateTimeForInput(now.toISOString()),
        endTime: formatDateTimeForInput(endTime.toISOString())
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedStudent = students.find(s => s.id === formData.studentId);
  const availableSubjects = selectedStudent?.studentProfile?.subjects && selectedStudent.studentProfile.subjects.length > 0 
    ? selectedStudent.studentProfile.subjects 
    : subjects;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Suggest Booking to Student</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Success/Error Messages */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
              <div className="text-green-600">{success}</div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <div className="text-red-600">{error}</div>
            </div>
          )}

          {/* Student Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Select Student
            </label>
            <select
              name="studentId"
              value={formData.studentId}
              onChange={handleInputChange}
              required
              className="input w-full"
            >
              <option value="">Choose a student...</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.name} ({student.email})
                </option>
              ))}
            </select>
          </div>

          {/* Subject Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <BookOpen className="w-4 h-4 inline mr-1" />
              Subject
            </label>
            <select
              name="subjectId"
              value={formData.subjectId}
              onChange={handleInputChange}
              required
              className="input w-full"
            >
              <option value="">Choose a subject...</option>
              {availableSubjects.map(subject => (
                <option key={subject.id} value={subject.id}>
                  {subject.name}
                </option>
              ))}
            </select>
            {selectedStudent && selectedStudent.studentProfile?.subjects && selectedStudent.studentProfile.subjects.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Showing subjects from {selectedStudent.name}'s profile
              </p>
            )}
          </div>

          {/* Lesson Type & Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lesson Details
            </label>
            <div className="grid grid-cols-2 gap-4">
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="input w-full"
              >
                <option value="LESSON">Lesson</option>
                <option value="CONSULTATION">Consultation</option>
              </select>
              
              <select
                name="isOnline"
                value={formData.isOnline ? 'online' : 'inperson'}
                onChange={(e) => setFormData(prev => ({ ...prev, isOnline: e.target.value === 'online' }))}
                className="input w-full"
              >
                <option value="online">Online</option>
                <option value="inperson">In-Person</option>
              </select>
            </div>
          </div>

          {/* Custom Title (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <BookOpen className="w-4 h-4 inline mr-1" />
              Custom Lesson Title (Optional)
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Leave empty for default format (Subject - Online/In Person)"
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              If left empty, will use default format: {subjects.find(s => s.id === formData.subjectId)?.name || 'Subject'} - {formData.isOnline ? 'Online' : 'In Person'}
            </p>
          </div>

          {/* Start and End Times */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FlexibleTimeInput
              label="Start Time"
              value={formData.startTime}
              onChange={(value) => setFormData(prev => ({ ...prev, startTime: value }))}
            />
            <FlexibleTimeInput
              label="End Time"
              value={formData.endTime}
              onChange={(value) => setFormData(prev => ({ ...prev, endTime: value }))}
            />
          </div>

          {/* Price Display */}
          {formData.subjectId && formData.startTime && formData.endTime && (() => {
            const startTime = new Date(formData.startTime);
            const endTime = new Date(formData.endTime);
            const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
            const durationMinutes = Math.round(durationHours * 60);
            
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-900">Suggested Price:</span>
                  <span className="text-lg font-bold text-blue-900">
                    £{calculatePrice().toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-blue-700 mt-1">
                  {formatDuration(durationHours)} • {formData.isOnline ? 'Online' : 'In-person'} • {formData.type.toLowerCase()}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {startTime.toLocaleString('en-GB', { 
                    weekday: 'short', month: 'short', day: 'numeric', 
                    hour: 'numeric', minute: '2-digit', hour12: true 
                  })} - {endTime.toLocaleTimeString('en-GB', { 
                    hour: 'numeric', minute: '2-digit', hour12: true 
                  })}
                </p>
              </div>
            );
          })()}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes for Student (Optional)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="input w-full"
              placeholder="Add any additional information about this lesson suggestion..."
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suggesting...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Suggest Booking
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TutorBookingSuggestion;